import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Channel, Device } from "../types";

interface DevicesState {
  devices: Device[];
  channelsByDevice: Record<number, Channel[]>;
  setDevices: (d: Device[]) => void;
  setChannels: (deviceId: number, ch: Channel[]) => void;
  patchDevice: (id: number, patch: Partial<Device>) => void;
  clear: () => void;
}

// Se persiste en localStorage para que al recargar (F5) la lista se muestre
// instantaneamente desde cache, mientras react-query refetchea en segundo
// plano y reemplaza la data por la fresca. Sin persistencia, el sidebar se
// queda con "No hay dispositivos" hasta que la query termina.
export const useDevicesStore = create<DevicesState>()(
  persist(
    (set) => ({
      devices: [],
      channelsByDevice: {},
      setDevices: (devices) => set({ devices }),
      setChannels: (deviceId, ch) =>
        set((s) => ({ channelsByDevice: { ...s.channelsByDevice, [deviceId]: ch } })),
      patchDevice: (id, patch) =>
        set((s) => ({
          devices: s.devices.map((d) => (d.id === id ? { ...d, ...patch } : d)),
        })),
      clear: () => set({ devices: [], channelsByDevice: {} }),
    }),
    { name: "vms-devices" },
  ),
);

// Selector helper: cantidad y proporcion de devices accesibles que estan
// reportados como offline. El banner del Layout lo usa para alertar al
// operador cuando hay degradacion en la conectividad (VPN flapping,
// monitor de health no llega a los NVRs, etc.) sin agregar polling extra.
export function getOfflineStats(devices: Device[]): { offline: number; total: number; ratio: number } {
  if (devices.length === 0) return { offline: 0, total: 0, ratio: 0 };
  const accessible = devices.filter((d) => d.is_enabled !== false);
  const offline = accessible.filter((d) => !d.is_online).length;
  const total = accessible.length;
  return { offline, total, ratio: total > 0 ? offline / total : 0 };
}
